import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  MessageSquare,
  MessagesSquare,
  Mail,
  ArrowRight,
  Users,
  Zap,
  Star,
  BookOpen,
  Workflow,
  Box,
  Share2,
  CircleDollarSign,
  HeartPulse,
  ConciergeBell,
  Landmark,
  GraduationCap,
  ShoppingBag,
  Droplet,
  Store,
} from 'lucide-react';
import freshroadLogo from '../../../images/freshroadlogo.png';
import {
  T,
  SURFACE,
  HAIRLINE,
  SHADOW,
  Hero,
  DemoLink,
  CenterHead,
  StatStrip,
  IconStrip,
} from './siteUI';

/* =========================================================
   Landing page — the introduction to the three products.

   Everything here funnels toward Conversational AI, Teammate and Orbiter.
   Product detail lives on the product pages; this page only has to make the
   three legible and get the reader into one of them.
   ========================================================= */

/* Each product carries its own colour. This is the one place on the site where
   hue is informative rather than decorative: three sibling products a reader
   has to tell apart. The colour is set here once and used by both the hero
   orbit and the product cards, so an outcome and its product always match.
   Classes are written out in full — Tailwind's scanner reads raw source. */
export const ACCENTS = {
  violet: {
    card: 'bg-violet-50/50 ring-violet-200/80',
    tile: 'bg-violet-100 text-violet-600',
    chip: 'text-violet-500',
    link: 'text-violet-700 hover:text-violet-800 focus-visible:outline-violet-600',
  },
  emerald: {
    card: 'bg-emerald-50/50 ring-emerald-200/80',
    tile: 'bg-emerald-100 text-emerald-600',
    chip: 'text-emerald-500',
    link: 'text-emerald-700 hover:text-emerald-800 focus-visible:outline-emerald-600',
  },
  orange: {
    card: 'bg-orange-50/50 ring-orange-200/80',
    tile: 'bg-orange-100 text-orange-600',
    chip: 'text-orange-500',
    link: 'text-orange-700 hover:text-orange-800 focus-visible:outline-orange-600',
  },
};

/* The three orbiting outcomes — one per product, same order and same colour as
   the product cards further down. */
const ORBIT = [
  { name: 'Grow your business', sub: 'Win more customers', icon: MessagesSquare, accent: 'violet' },
  { name: 'Scale your workforce', sub: "Multiply your team's impact", icon: Users, accent: 'emerald' },
  { name: 'Run AI efficiently', sub: 'Optimize every model and workflow', icon: Zap, accent: 'orange' },
];

const PRODUCTS = [
  {
    name: 'Conversational AI',
    accent: 'violet',
    sub: 'AI for customer conversations',
    icon: MessagesSquare,
    to: '/site/conversational-ai',
    chips: [
      { label: 'Voice', icon: Phone },
      { label: 'SMS', icon: MessageSquare },
      { label: 'Chat', icon: MessagesSquare },
      { label: 'Email', icon: Mail },
    ],
  },
  {
    name: 'Teammate',
    accent: 'emerald',
    sub: 'AI employees for your business',
    icon: Users,
    to: '/site/teammate',
    chips: [
      { label: 'Skills', icon: Star },
      { label: 'Knowledge', icon: BookOpen },
      { label: 'Workflows', icon: Workflow },
    ],
  },
  {
    name: 'Orbiter',
    accent: 'orange',
    sub: 'AI infrastructure for your AI stack',
    icon: Zap,
    to: '/site/orbiter',
    chips: [
      { label: 'Models', icon: Box },
      { label: 'Routing', icon: Share2 },
      { label: 'Cost optimization', icon: CircleDollarSign },
    ],
  },
];

/* Screening figures come from the Western Sierra Health Clinic deployment. */
const STATS = [
  { value: '23% → 33.8%', label: 'Increase in successful cohort screening rate' },
  { value: '89.4%', label: 'Patient satisfaction achieved' },
  { value: 'Up to 94%', label: 'Reduction in customer operation costs' },
  { value: '3.5x', label: 'Increase in team productivity' },
];

const INDUSTRIES = [
  { name: 'Healthcare', icon: HeartPulse },
  { name: 'Hotels', icon: ConciergeBell },
  { name: 'Financial Services', icon: Landmark },
  { name: 'Education', icon: GraduationCap },
  { name: 'Retail & eCommerce', icon: ShoppingBag },
  { name: 'Blood Banks', icon: Droplet },
  { name: 'Main Street Businesses', icon: Store },
];

/* ---------- hero orbit ---------- */

const OrbitCard = ({ name, sub, icon: Icon, accent, className = '' }) => (
  <div className={`flex w-full max-w-[248px] items-start gap-3 p-3.5 ${SURFACE.card} ${className}`}>
    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${ACCENTS[accent].tile}`}>
      <Icon size={17} strokeWidth={1.9} />
    </span>
    <span className="min-w-0">
      <span className="block text-[13px] font-semibold leading-tight">{name}</span>
      <span className={`mt-0.5 block ${T.micro}`}>{sub}</span>
    </span>
  </div>
);

const OrbitDiagram = () => (
  <div className="relative mx-auto flex w-full max-w-[560px] flex-col items-center gap-4 md:h-[440px] md:gap-0">
    {/* Decorative scaffolding for the three cards — hidden from assistive tech,
        and only drawn at widths where the absolute layout applies. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-[46px] hidden h-[320px] w-[320px] -translate-x-1/2 rounded-full border-2 border-dashed border-teal-200 md:block"
    />

    <OrbitCard {...ORBIT[0]} className="md:absolute md:left-1/2 md:top-0 md:-translate-x-1/2" />

    <div
      className={`relative z-10 flex h-[190px] w-[190px] shrink-0 flex-col items-center justify-center rounded-full bg-white md:absolute md:left-1/2 md:top-[112px] md:-translate-x-1/2 ${HAIRLINE} shadow-[0_0_0_14px_rgba(20,184,166,0.06),0_0_0_30px_rgba(20,184,166,0.03),0_2px_4px_rgba(15,23,42,0.05),0_16px_40px_-16px_rgba(15,23,42,0.2)]`}
    >
      <img src={freshroadLogo} alt="" className="h-11 w-11 object-contain" />
      <p className="mt-2 text-[17px] font-bold tracking-[-0.02em]">FreshRoad</p>
      <p className={T.micro}>Business AI Platform</p>
    </div>

    <OrbitCard {...ORBIT[1]} className="md:absolute md:bottom-0 md:left-0" />
    <OrbitCard {...ORBIT[2]} className="md:absolute md:bottom-0 md:right-0" />
  </div>
);

/* ---------- product card ---------- */

const ProductCard = ({ name, sub, icon: Icon, to, chips, accent }) => {
  const a = ACCENTS[accent];
  return (
  <div
    className={`flex flex-col rounded-2xl p-6 ring-1 ${a.card} ${SHADOW.card} transition-shadow duration-200 hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_16px_40px_-16px_rgba(15,23,42,0.2)]`}
  >
    <div className="flex items-start gap-3.5">
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${a.tile}`}>
        <Icon size={22} strokeWidth={1.85} />
      </span>
      <div className="min-w-0">
        <p className={T.h3}>{name}</p>
        <p className={`mt-1 ${T.micro}`}>{sub}</p>
      </div>
    </div>

    <div className="mt-5 flex flex-wrap gap-2">
      {chips.map(({ label, icon: ChipIcon }) => (
        <span
          key={label}
          className={`inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-[12px] font-semibold ${HAIRLINE}`}
        >
          <ChipIcon size={13} strokeWidth={2} className={a.chip} />
          {label}
        </span>
      ))}
    </div>

    <div className="mt-6 flex-1" />
    <Link
      to={to}
      className={`inline-flex w-fit items-center gap-1.5 rounded-lg text-[13.5px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${a.link}`}
    >
      Learn more <ArrowRight size={15} />
    </Link>
  </div>
  );
};

/* ---------- page ---------- */

const DemoSiteLanding = () => (
  <>
    {/* Hero */}
    <section className="grid grid-cols-1 items-center gap-10 py-8 lg:grid-cols-[1fr_1.05fr]">
      <Hero
        title={
          <>
            Business AI for <span className="text-teal-600">every business.</span>
          </>
        }
        actions={
          <>
            <DemoLink>
              Book a demo <ArrowRight size={16} />
            </DemoLink>
          </>
        }
        footer={
          <>
            <p className="max-w-[30ch] text-[19px] font-bold leading-[1.28] tracking-[-0.016em]">
              Give every business the capabilities of a{' '}
              <span className="text-teal-600">1,000-person team.</span>
            </p>
            <p className={`mt-2.5 max-w-[52ch] ${T.body}`}>
              FreshRoad brings together people, data, and AI so your business can scale without
              limits.
            </p>
          </>
        }
      >
        Win more customers. Scale your team. Eliminate AI complexity.
      </Hero>

      <OrbitDiagram />
    </section>

    {/* The three products */}
    <section className="px-2 py-10">
      <CenterHead
        title="One AI platform, built to run your business."
        subtitle="FreshRoad brings customer AI, AI workers, and AI infrastructure together in one platform."
      />
      <div className="mt-9 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {PRODUCTS.map((p) => (
          <ProductCard key={p.name} {...p} />
        ))}
      </div>
    </section>

    {/* Results */}
    <section className={`px-6 py-10 md:px-10 ${SURFACE.band}`}>
      <CenterHead title="Real results. Measurable impact." />
      <div className="mt-8">
        <StatStrip stats={STATS} />
      </div>
    </section>

    {/* Industries */}
    <section className="px-2 py-10">
      <CenterHead title="Built for impact across industries." />
      <div className="mt-9">
        <IconStrip items={INDUSTRIES} />
      </div>
    </section>

    {/* Closing CTA */}
    <section className={`flex flex-col items-center gap-7 px-8 py-10 md:flex-row md:px-12 ${SURFACE.band}`}>
      <span className="hidden h-20 w-20 shrink-0 items-center justify-center rounded-full bg-teal-50 md:flex">
        <img src={freshroadLogo} alt="" className="h-10 w-10 object-contain" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-balance text-[22px] font-bold leading-[1.25] tracking-[-0.02em]">
          We&rsquo;re tackling the hard problems of running a business.
        </p>
        <p className={`mt-2 max-w-[58ch] ${T.body}`}>
          FreshRoad helps businesses win more customers, scale their teams, and reduce AI complexity.
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <DemoLink>
          Book a demo <ArrowRight size={16} />
        </DemoLink>
      </div>
    </section>
  </>
);

export default DemoSiteLanding;
